import { create } from 'zustand';
import { format, addDays, subDays } from 'date-fns';
import calendarApi from '../services/calendarApi';
import type { CalendarDateResponse } from '../types/calendar';

interface CalendarState {
    // Current selected date
    currentDate: Date;

    // Calendar data cache
    calendarData: CalendarDateResponse | null;

    // Loading states
    isLoading: boolean;
    error: string | null;

    // Actions
    setDate: (date: Date) => void;
    goToToday: () => void;
    goToPrevDay: () => void;
    goToNextDay: () => void;
    fetchCalendarData: () => Promise<void>;
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
    currentDate: new Date(),
    calendarData: null,
    isLoading: false,
    error: null,

    setDate: (date: Date) => {
        set({ currentDate: date });
        get().fetchCalendarData();
    },

    goToToday: () => {
        set({ currentDate: new Date() });
        get().fetchCalendarData();
    },

    goToPrevDay: () => {
        const { currentDate } = get();
        set({ currentDate: subDays(currentDate, 1) });
        get().fetchCalendarData();
    },

    goToNextDay: () => {
        const { currentDate } = get();
        set({ currentDate: addDays(currentDate, 1) });
        get().fetchCalendarData();
    },

    fetchCalendarData: async () => {
        const { currentDate } = get();
        const dateStr = format(currentDate, 'yyyy-MM-dd');

        set({ isLoading: true, error: null });

        try {
            const data = await calendarApi.getDate(dateStr);
            set({ calendarData: data, isLoading: false });
        } catch (error) {
            set({
                error: 'Failed to load calendar data',
                isLoading: false,
            });
            console.error('Calendar fetch error:', error);
        }
    },
}));

export default useCalendarStore;
